import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { userAppDataContext, selectDeviceKVStore, fetchDeviceKVStore, updateDeviceKVStore, selectHomeKVStore, fetchHomeKVStore }
    from '@moderepo/mode-data-state-user-app';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import * as SCUtils from './SCUtils';
import { SCSensorTableDataItem, SCSensorsTable, SCSensorsTableProps, SCSensorsTableUpdatableInputs }
    from '../../components/sensor-cloud/SCSensorsTable';



export interface SCSensorsTableContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly moduleId: string;

    // The container, 'home' or 'device', which the sensor modules belonged to and it's ID, 'homeId' or 'deviceId'.
    readonly smContainer: SCUtils.SensorModuleContainer;
    readonly smContainerId: number;
}


/**
 * This is as wrapper for SCSensorsTable component. This container contains the logic for the SCSensorsTable
 */
export const SCSensorsTableContainer: React.FC<SCSensorsTableContainerProps> = (props: SCSensorsTableContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);


    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        const key = SCUtils.getSensorModuleKVStoreKey(props.moduleId);
        const kvPair = props.smContainer === SCUtils.SensorModuleContainer.DEVICE
            ? selectDeviceKVStore(appDataState, props.smContainerId, key)
            : selectHomeKVStore(appDataState, props.smContainerId, key);
        if (key) {
            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SCUtils.SensorCloudKeyValuePair;
        }
        return undefined;
    }, [appDataState, props.smContainer, props.smContainerId, props.moduleId]);


    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return SCUtils.fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);



    /**
     * Load the key/value pair for the pairs that are used for storing this sensor module info.
     */
    const loadKeyValuePair = useCallback(() => {
        const key = SCUtils.getSensorModuleKVStoreKey(props.moduleId);

        if (props.smContainer === SCUtils.SensorModuleContainer.DEVICE) {
            appDataDispatch(fetchDeviceKVStore(props.smContainerId, key)).catch(() => {
                /* ignore error */
            });
        } else {
            appDataDispatch(fetchHomeKVStore(props.smContainerId, key)).catch(() => {
                /* ignore error */
            });
        }
    }, [appDataDispatch, props.smContainer, props.smContainerId, props.moduleId]);



    /**
     * This useEffect is used for loading sensor module's key/value pair on component load
     */
    useEffect(() => {
        loadKeyValuePair();
    }, [loadKeyValuePair]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);



    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    /**
     * Handler for when the user clicked Save input from the Edit sensor list form
     */
    const onSensorListFormSubmit = useCallback(async (inputs: SCSensorsTableUpdatableInputs) => {
        if (project && sensorModuleKVStore && sensorModule) {

            const { sensorsStatus } = inputs;

            const newSensorValueNamesList = Object.entries(sensorsStatus).filter(([, enabled]): boolean => {
                return enabled;
            }).map(([sensor]): string => {
                return sensor;
            }).sort();

            // make sure new sensor list is different before making API call to update so that we don't waste an API call
            if (!sensorModule.sensors || sensorModule.sensors.join(',') !== newSensorValueNamesList.join(',')) {
                try {
                    // dispatch an action to update the sensor module (kv pair)
                    const newKVPairValue = {
                        ...sensorModule,
                        sensors: newSensorValueNamesList,
                    };

                    // TODO - Maybe need to update Home KV too
                    await appDataDispatch(updateDeviceKVStore(Number(sensorModule.gatewayId), sensorModuleKVStore.key, newKVPairValue));

                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.sensor_module' }));
                } catch (error) {
                    // ignore error
                }
            }

            closeEditing();
        }
    }, [project, sensorModuleKVStore, sensorModule, closeEditing, appDataDispatch, uiDispatch]);



    /**
     * Localize the list of sensors data before passing it to the Sensor table for display
     */
    const localizeSensorsTableData = useCallback((): readonly SCSensorTableDataItem[] | undefined => {
        if (sensorModule) {

            // Get the name of all the sensors that this sensor module have
            const sensors = SCUtils.getAvailableSensorsList(sensorModule);

            // check if this sensor module has a type defined in the catalog
            const sensorModuleType = SCUtils.sensorModuleCatalogMap.get(sensorModule.modelId);
            const sensorDisplayNames = sensorModuleType ? sensorModuleType.moduleSchemaDisplayNames : undefined;

            return sensors.map((sensorValueName: string): SCSensorTableDataItem => {

                const sensorInfo = {
                    name       : sensorValueName,
                    displayName: sensorDisplayNames ? sensorDisplayNames[sensorValueName] : undefined,
                    enabled    : sensorModule.sensors && sensorModule.sensors.includes(sensorValueName),
                };

                return {
                    actualValue : sensorInfo,
                    displayValue: {
                        name       : sensorInfo.name,
                        displayName: sensorInfo.displayName ? sensorInfo.displayName : trans('label.none'),
                        enabled    : capitalizeAll(trans(sensorInfo.enabled ? 'label.enabled' : 'label.disabled')),
                    },
                };
            });
        }

        return undefined;
    }, [sensorModule, trans]);



    /**
     * Create inputSettings for the SensorsTableComp. Since we don't know the input we will have ahead of time, we can only setup the
     * cancelButton and saveButtons. Everything else will be created dynamically.
     */
    const fieldsSettings = useMemo((): SCSensorsTableProps['fieldsSettings'] | undefined => {
        if (sensorModule) {
            return {
                fields: {
                    name: {
                        dataItemProp: 'name',
                        label       : capitalizeAll(trans('label.name')),
                    },
                    displayName: {
                        dataItemProp: 'displayName',
                        label       : capitalizeAll(trans('label.display_name')),
                        hidden      : SCUtils.sensorModuleCatalogMap.get(sensorModule.modelId) === undefined,
                    },
                    enabled: {
                        dataItemProp: 'enabled',
                        label       : capitalizeAll(trans('label.sensing')),
                    },
                },
            };
        }
        return undefined;
    }, [sensorModule, trans]);


    /**
     * The SCSensorsTable is also an Info Component therefore it has a 'fields' map of fields but it will be empty
     */
    const fields = useMemo(() => {
        return {};
    }, []);



    const isFetchingData = Boolean(
        !project
        || !sensorModuleKVStore,
    );


    return (
        <>
            {!isFetchingData && fieldsSettings && (
                <SCSensorsTable
                    className={clsx(props.className, isEditing && 'editing')}
                    isEditing={isEditing}
                    listData={localizeSensorsTableData()}
                    title={capitalizeAll(trans('terms.sensor', { count: 2 }))}
                    icon="settings_input_antenna"
                    fields={fields}
                    fieldsSettings={fieldsSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.sensor', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.sensor', { count: 2 })),
                            })}
                        />
                    )}

                    actions={{
                        /*
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        */
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onSensorListFormSubmit,
                        },
                    }}
                />
            )}

            {isFetchingData && (
                <CircularProgress />
            )}
        </>
    );
};
